/**
 * The Payroll module's route table (CLAUDE.md §8.12).
 *
 * **Every route names the permission the server guards the screen's primary
 * read with, never the write.** The run screen is gated on `payroll.run.read`
 * and decides per action whether to render Calculate, Review, Approve, Post and
 * Disburse — five separately permissioned decisions §6 declares a
 * segregation-of-duties chain. Gating the route on any one of them would hide
 * the whole run from everyone else in the chain.
 *
 * `/payroll/components/new` is the one exception, and deliberately so: it has no
 * read-only mode, it exists to create a formula. It is declared **before**
 * `/payroll/components/:id(\d+)` so `new` can never be parsed as an id.
 *
 * There is no `/payroll/runs/new` route. A run is four fields and a period, and
 * creating one from a dialog on the list keeps the list — with the previous
 * month's run visible beside it — in view, which is precisely what §11's
 * "never lose page context" asks for.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PAYROLL_PERMISSIONS } from '@/modules/payroll/permissions'

export const PAYROLL_ROUTES: RouteRecordRaw[] = [
  {
    path: '/payroll/runs',
    name: 'payroll-runs',
    component: () => import('@/modules/payroll/RunsListView.vue'),
    meta: { titleKey: 'payroll.routeTitles.runs', permission: PAYROLL_PERMISSIONS.runRead },
  },
  {
    path: '/payroll/runs/:id(\\d+)',
    name: 'payroll-run',
    component: () => import('@/modules/payroll/RunView.vue'),
    meta: { titleKey: 'payroll.routeTitles.run', permission: PAYROLL_PERMISSIONS.runRead },
  },
  {
    path: '/payroll/payslips/:id(\\d+)',
    name: 'payroll-payslip',
    component: () => import('@/modules/payroll/PayslipView.vue'),
    meta: { titleKey: 'payroll.routeTitles.payslip', permission: PAYROLL_PERMISSIONS.payslipRead },
  },
  {
    path: '/payroll/components',
    name: 'payroll-components',
    component: () => import('@/modules/payroll/ComponentsListView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.components',
      permission: PAYROLL_PERMISSIONS.componentRead,
    },
  },
  {
    path: '/payroll/components/new',
    name: 'payroll-component-new',
    component: () => import('@/modules/payroll/ComponentView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.componentNew',
      permission: PAYROLL_PERMISSIONS.componentManage,
    },
  },
  {
    path: '/payroll/components/:id(\\d+)',
    name: 'payroll-component',
    component: () => import('@/modules/payroll/ComponentView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.component',
      permission: PAYROLL_PERMISSIONS.componentRead,
    },
  },
]
